import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ModuleActivityEnum, ProjectResponse } from 'services/tenantManagementService';
import { SmartContainer, SmartItem } from 'components/SmartContainer/SmartContainer';
import { RootState } from 'base/reducer/reducer';
import { ColumnContainer } from 'components/Layout';
import { Subtract } from 'utility-types';
import { UserRolePermissionEnum } from '../projectHooks';
import { ProjectMultiSelect } from '../controls/ProjectMultiSelect';

export type ComponentProjectsProps = {
	projects: ProjectResponse[]
	disabledEdit?: boolean
}

export function WithProjectsPicker<P extends ComponentProjectsProps>(
	Component: React.ComponentType<P>,
	moduleEnum: ModuleActivityEnum,
	useMargin?: boolean,
	teamMemberPermission?: string,
	userRolePermission?: UserRolePermissionEnum[]) {

	const HighOrderComponent = (props: Subtract<P, ComponentProjectsProps>) => {
		const persistedProject = useSelector((state: RootState) => state.persistedProject);
		const persistedDefaultProject = useSelector((state: RootState) => state.persistedDefaultProject);

		// initial state should be the one from URL, and later on, state from URL should be ignored
		const [selectedProjectIds, setSelectedProjectIds] = useState<number[] | undefined>(persistedDefaultProject.value ? [persistedDefaultProject.value.projectId] : []);

		const selectedProjects = useMemo(
			() => {
				if (selectedProjectIds && selectedProjectIds.length > 0) {
					return persistedProject.items.filter(x => selectedProjectIds.includes(x.id))
				}

				return undefined;
			},
			[selectedProjectIds, persistedProject]
		)

		const WrapperComponent = useMemo(
			() => useMargin ? ColumnContainer : React.Fragment,
			[]
		)

		const componentPropsMemo = useMemo(
			() => {
				return {
					...props,
					projects: selectedProjects,
					// TODO: TICKETS check how do we know is change allowed
					// disabledEdit: !isStatusBySemantic(ProjectStatusEnum.Released, selectedItemProject?.statusId, persistedProjectStatus.itemsMap)
				} as P
			},
			[selectedProjects, props]
		)

		return (
			<WrapperComponent>
				<SmartContainer>
					<SmartItem>
						<ProjectMultiSelect
							value={selectedProjectIds}
							onChange={setSelectedProjectIds}
							moduleEnum={moduleEnum}
							showCompleted
							teamMemberPermission={teamMemberPermission}
							userRolePermission={userRolePermission}
						/>
					</SmartItem>
				</SmartContainer>
				{(selectedProjectIds && selectedProjectIds.length > 0) && <Component {...componentPropsMemo} />}
			</WrapperComponent>
		)
	}

	return HighOrderComponent;
}
